/**
 * BarreDActions — une rangée de contrôles alignés (02-11-2026) : des boutons, un champ, un
 * sélecteur, des onglets en segments, un rond à icône, tous de la même taille, sur UNE ligne et le
 * même axe ; ça passe à la ligne proprement quand la place manque ; une étiquette se range DANS la
 * ligne (« FLOW  [Démarrer] [Pause] »), jamais au-dessus de son bouton.
 *
 * Kayla, 02-11, devant la fiche du fan en prod : « c'est pas très bien aligné, il y a plein de
 * trucs où c'est un peu mal aligné » — un rond plus petit que les pilules, une icône nue, une
 * étiquette qui flotte. La rangée a la hauteur de l'échelle (`CLAUDE.md`, « L'échelle ») et
 * centre ses enfants dessus : un contrôle d'une autre taille se voit tout de suite.
 *
 *   <BarreDActions etiquette="Flow">
 *     <Button>Démarrer</Button>
 *     <Button variant="secondary">Pause</Button>
 *     <Select value={v} onChange={…} className="w-40">…</Select>
 *     <Button variant="ghost" iconOnly icon={<Trash2 />} aria-label="Supprimer" />
 *   </BarreDActions>
 *
 *   <BarreDActions>                                  ← deux groupes, aux deux bords
 *     <Button variant="ghost">Retour</Button>
 *     <BarreDActions.Espace />
 *     <Button>Enregistrer</Button>
 *   </BarreDActions>
 */
import type { ReactNode } from "react";

import { Etiquette } from "./Etiquette";
import { cn } from "./utils";
import "./surfaces.css";

export type TailleBarre = "sm" | "md" | "lg";

export interface BarreDActionsProps {
  children: ReactNode;
  /** L'étiquette, dans la ligne, avant les contrôles. */
  etiquette?: ReactNode;
  /** La taille des contrôles qu'elle porte : sa hauteur, l'axe. Défaut `md` (40 px). */
  taille?: TailleBarre;
  /** D'où partent les contrôles. Défaut `gauche` ; `BarreDActions.Espace` pousse ce qui suit à droite. */
  alignement?: "gauche" | "droite" | "centre";
  /** Sans retour à la ligne : la barre défile de côté (une barre d'outils sur un téléphone). */
  defile?: boolean;
  /** Le nom du groupe pour un lecteur d'écran (« Actions du flow »). */
  ariaLabel?: string;
  className?: string;
}

export function BarreDActions({ children, etiquette, taille = "md", alignement = "gauche", defile = false, ariaLabel, className }: BarreDActionsProps) {
  return (
    <div
      role={ariaLabel ? "group" : undefined}
      aria-label={ariaLabel}
      className={cn("ba", defile ? "ba-defile" : "ba-plie", className)}
      data-taille={taille}
      data-alignement={alignement}
    >
      {etiquette !== undefined && etiquette !== null && <Etiquette className="ba-etiquette">{etiquette}</Etiquette>}
      {children}
    </div>
  );
}

/** L'espace élastique entre deux groupes d'une barre : ce qui suit va au bord droit. */
function Espace() {
  return <span className="ba-espace" aria-hidden />;
}

BarreDActions.Espace = Espace;
